import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import * as api from '~/api/authentication';
import * as DimensionsApi from '~/api/dimensions';
import type { CredentialsRequest, LoginRequest } from '~/types/api-types';
import { AuthStatus, type Jwt, type Roles, type User } from '~/types/types';

export async function sendTwoFA(args: CredentialsRequest) {
  return await api.sendTwoFA(args);
}

const refreshIntervalSeconds = 60;
// Refresh if less than one hour left. Refreshing is a very cheap operation anyway
const refreshThresholdSeconds = 60 * 60;
export const tokenKey = 'JWT_TOKEN';
// The return type of setInterval is number in browsers and NodeJS.Timeout
// in NodeJS for some reason.
let refreshCheckIntervalHandle: NodeJS.Timeout | number | null = null;

const jwtSubject: BehaviorSubject<Jwt | null> = new BehaviorSubject<Jwt | null>(null);
let userObs: Observable<User | null>;

export async function initialize() {
  const currentJwt = getJwtFromStorage();
  userObs = jwtSubject.asObservable().pipe(
    map((jwt: Jwt | null) => {
      return jwt ? jwt.user : null;
    })
  );

  jwtSubject.next(currentJwt);
  const valid = await isTokenValid(true);
  if (valid) {
    startRefreshInterval();
  } else {
    clearJwt();
  }
}

export function getRawJwt() {
  let sessionJwt = sessionStorage.getItem(tokenKey);
  if (sessionJwt === null) {
    sessionJwt = localStorage.getItem(tokenKey);
  }
  return sessionJwt;
}

export function isAuthenticated() {
  return getRawJwt() !== null;
}

export function getUserObs(): Observable<User | null> {
  return userObs;
}

export function getAuthStatusObs(acceptableRoles: Readonly<Roles[]>): Observable<AuthStatus> {
  return userObs.pipe(
    map((user: User | null) => {
      if (user === null) {
        return AuthStatus.Unauthenticated;
      }

      if (user.temporaryPassword) {
        return AuthStatus.AuthorizedTemporaryPassword;
      }

      // Empty role list for components that require login but no specific role
      if (acceptableRoles.length === 0) {
        return AuthStatus.Authorized;
      }
      const anyRoleMatching = acceptableRoles.some((role) => user.roles.includes(role));
      return anyRoleMatching ? AuthStatus.Authorized : AuthStatus.Unauthorized;
    })
  );
}

// With JWTs we don't have to make an api call, but we still do logout
// here in order to make this module responsible for all auth handling
export async function logout() {
  clearJwt();
  sessionStorage.removeItem(DimensionsApi.dimensionTypesCacheKey);
  document.cookie = `downloadToken=; path=/api/submissions/; expires=${new Date(0).toUTCString()}`;
  stopRefreshInterval();
}

export async function credentialsCheck(args: CredentialsRequest, persistent = false) {
  const response = await api.credentialsCheck(args);
  if (response.data !== '') {
    setJwt(response.data.token, persistent);
    startRefreshInterval();
    return true;
  }

  return false;
}

export async function login(args: LoginRequest, persistent = false) {
  const response = await api.login(args);
  setJwt(response.token, persistent);
  startRefreshInterval();
}

export async function changePass(userId: number, oldPassword: string, newPassword: string) {
  await api.changePass({ userId, oldPassword, newPassword });

  try {
    const newToken = await api.refresh();
    if (newToken === null) {
      throw new Error('Failed to refresh session token');
    }
    const wasPersistent = localStorage.getItem(tokenKey) !== null;
    setJwt(newToken.token, wasPersistent, false);
  } catch (error) {
    // Nothing to do if refresh failed
  }
}

export async function resetPass(username: string) {
  return api.resetPass(username);
}

function clearJwt() {
  sessionStorage.removeItem(tokenKey);
  localStorage.removeItem(tokenKey);
  jwtSubject.next(null);
}

function setJwt(token: string, persistent: boolean, clear = true) {
  if (clear) {
    clearJwt(); // Clear to make sure we don't have some old token lying around
  }

  if (persistent) {
    localStorage.setItem(tokenKey, token);
  } else {
    sessionStorage.setItem(tokenKey, token);
  }
  try {
    const decoded = jwtDecode(token) as any as Jwt;
    if (jwtSubject && decoded) {
      jwtSubject.next(decoded);
    }
  } catch (error) {
    throw new Error('Token har fel format');
  }
}

function getJwtFromStorage() {
  try {
    const token = getRawJwt();
    if (token === null) {
      return null;
    }

    const decoded = jwtDecode(token) as any;
    return decoded as Jwt;
  } catch (error: any) {
    throw new Error(`Kunde inte hämta inloggad användare: ${error.message}`);
  }
}

async function doTokenRefresh() {
  if (!isTokenValid(false)) {
    clearJwt();
    stopRefreshInterval();
    return;
  }

  // The RFC defining JWTs requires expiration to be in seconds,
  // but javascript expects milliseconds
  const user = getJwtFromStorage();
  const expiresAt = new Date(user!.exp * 1000).getTime();
  const now = new Date().getTime();

  const threshold = refreshThresholdSeconds * 1000;
  if (expiresAt - now > threshold) {
    return;
  }
  try {
    const newToken = await api.refresh();

    if (newToken === null) {
      throw new Error('Failed to refresh session token');
    }
    const wasPersistent = localStorage.getItem(tokenKey) !== null;

    setJwt(newToken.token, wasPersistent);
  } catch (error) {
    // Nothing to do if refresh failed
  }
}

function startRefreshInterval() {
  if (refreshCheckIntervalHandle !== null) {
    return;
  }
  refreshCheckIntervalHandle = setInterval(doTokenRefresh, refreshIntervalSeconds * 1000);
}

function stopRefreshInterval() {
  if (refreshCheckIntervalHandle === null) {
    return;
  }
  refreshCheckIntervalHandle = null;
  clearInterval(refreshCheckIntervalHandle as any);
}

async function isTokenValid(doServerRequest: boolean) {
  const jwt = getJwtFromStorage();
  if (jwt === null) {
    return false;
  }

  const expiresAt = new Date(jwt.exp * 1000).getTime();
  const now = new Date().getTime();
  if (now > expiresAt) {
    return false;
  }

  if (!doServerRequest) {
    // If we're not validating against the server, assume the token is valid, even if it might not be
    return true;
  }

  return await api.validate();
}
